import { useRef, useState } from 'react';
import { clockToVideo, MATCH_FORMATS, PERIOD_LABELS } from '../core/timeline';
import { formatClock, formatVideoTime, parseTime, periodName } from '../core/time';
import { useReel } from '../state/reel';
import { useApp } from '../state/store';
import { Button, Chip, Field, Icon, Panel, ScoreBug } from '../ui';
import { Player, type PlayerHandle } from '../ui/Player';
import { useHotkeys } from '../ui/hotkeys';

function ClockField({ period, value, onChange }: { period: number; value: number; onChange: (sec: number) => void }) {
  const [draft, setDraft] = useState<string | null>(null);
  const bad = draft !== null && parseTime(draft) === null;
  return (
    <Field
      label={`Clock at ${periodName(period)} kickoff`}
      mono
      value={draft ?? formatClock(value)}
      error={bad ? 'Use mm:ss, like 45:00' : undefined}
      onChange={(e) => {
        setDraft(e.target.value);
        const v = parseTime(e.target.value);
        if (v !== null) onChange(v);
      }}
      onBlur={() => setDraft(null)}
    />
  );
}

export function SyncScreen() {
  const { project, update, setStep, url, info } = useApp();
  const { needed, missingKickoffs, parsed, clips } = useReel();
  const player = useRef<PlayerHandle>(null);
  const [now, setNow] = useState(0);
  const duration = info?.duration ?? 0;

  useHotkeys({
    ' ': () => player.current?.toggle(),
    ArrowLeft: () => player.current?.seek(player.current.time() - 1),
    ArrowRight: () => player.current?.seek(player.current.time() + 1),
    'shift+ArrowLeft': () => player.current?.seek(player.current.time() - 10),
    'shift+ArrowRight': () => player.current?.seek(player.current.time() + 10),
  });

  const nextToSet = missingKickoffs[0];
  const setKickoff = (p: number, t: number) => update((pr) => ({ kickoffs: { ...pr.kickoffs, [p]: Math.round(t * 10) / 10 } }));
  const clearKickoff = (p: number) =>
    update((pr) => {
      const k = { ...pr.kickoffs };
      delete k[p];
      return { kickoffs: k };
    });
  const firstOf = (p: number) => parsed.moments.find((m) => m.period === p);
  const markers = needed.filter((p) => project.kickoffs[p] !== undefined).map((p) => ({ at: project.kickoffs[p], label: `K${p}` }));
  const first = firstOf(1);
  const example = first && project.kickoffs[1] !== undefined ? clockToVideo(1, first.timeSec, { kickoffs: project.kickoffs, clockAt: project.clockAt }) : null;

  if (!url) return null;
  return (
    <div className="app-grid-side">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24, minWidth: 0 }}>
        <div data-tour="player">

        <Player
          ref={player}
          url={url}
          duration={duration}
          clips={clips.map((c) => ({ start: c.start, end: c.end, state: 'included' as const }))}
          markers={markers}
          onTime={setNow}
          topLeft={(t) => <ScoreBug time={formatVideoTime(t)} label={nextToSet ? `${periodName(nextToSet)} kickoff?` : 'Kickoffs set'} />}
          transportEnd={
            project.kickoffs[1] !== undefined && (
              <Button variant="overlay" size="sm" onClick={() => player.current?.seek(project.kickoffs[1])}>
                Jump to K1
              </Button>
            )
          }
        />
        </div>
        <Panel aria-labelledby="formula-title" style={{ flexDirection: 'row', alignItems: 'center', gap: 20 }}>
          <span style={{ width: 44, height: 44, flexShrink: 0, borderRadius: 'var(--radius-sm)', background: 'var(--cue-soft)', color: 'var(--cue)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon name="clock" size={22} />
          </span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <h2 id="formula-title" style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
              How clip times are found
            </h2>
            <p className="text-small muted" style={{ margin: 0 }}>
              Video time = kickoff in the video + (match clock − clock at kickoff).
              {first && example !== null && (
                <>
                  {' '}
                  The first timestamp, {periodName(1)} period {formatClock(first.timeSec)}, plays at <span className="time" style={{ color: 'var(--ink)' }}>{formatVideoTime(example)}</span>.
                </>
              )}
            </p>
          </div>
        </Panel>
      </div>

      <Panel aria-labelledby="sync-title" className="app-sticky" style={{ gap: 18 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <h1 id="sync-title" className="app-title">
            Sync the kickoffs
          </h1>
          <p className="text-small muted" style={{ margin: 0 }}>
            Scrub to the moment the ball is first played in each half, then set it. Space plays and pauses, the arrow keys step one second.
          </p>
        </div>
        <div data-tour="kickoffs" style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        {needed.map((p) => {
          const k = project.kickoffs[p];
          const f = firstOf(p);
          const firstVideo = f && k !== undefined ? clockToVideo(p, f.timeSec, { kickoffs: project.kickoffs, clockAt: project.clockAt }) : null;
          return (
            <div key={p} style={{ padding: 16, borderRadius: 'var(--radius-md)', background: k !== undefined ? 'var(--pitch-soft)' : 'transparent', border: k !== undefined ? '1px solid transparent' : '1px solid var(--line-strong)', display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div className="app-row">
                {k !== undefined ? <Chip tone="pitch" icon="check">Set</Chip> : <Chip>Not set</Chip>}
                <span style={{ fontSize: 15, fontWeight: 600 }}>{PERIOD_LABELS[p] ?? `Period ${p}`} kickoff</span>
                {k !== undefined && <span className="time" style={{ marginLeft: 'auto' }}>{formatVideoTime(k)}</span>}
              </div>
              {k === undefined ? (
                <Button variant={p === nextToSet ? 'primary' : 'secondary'} icon="flag" onClick={() => setKickoff(p, now)}>
                  Set at {formatVideoTime(now)}
                </Button>
              ) : (
                <div className="app-row" style={{ gap: 8, flexWrap: 'wrap' }}>
                  <Button size="sm" onClick={() => player.current?.seek(k)}>Jump to</Button>
                  <Button size="sm" onClick={() => setKickoff(p, now)}>Set at {formatVideoTime(now)}</Button>
                  <Button size="sm" variant="ghost" onClick={() => clearKickoff(p)}>Clear</Button>
                  {f && firstVideo !== null && (
                    <Button size="sm" variant="ghost" icon="play" onClick={() => player.current?.playRange(Math.max(0, firstVideo - 1), firstVideo + (f.durationSec ?? project.defaultLength) + 1)}>
                      Check first clip
                    </Button>
                  )}
                </div>
              )}
            </div>
          );
        })}
        </div>
        <div data-tour="clock" style={{ display: "flex", flexDirection: "column", gap: 14, paddingTop: 16, borderTop: '1px solid var(--line)' }}>
          <span className="app-label">Match format</span>
          <div className="hl-field">
            <label className="hl-label" htmlFor="fmt">
              Game length
            </label>
            <select
              id="fmt"
              className="hl-input"
              value={project.formatId}
              onChange={(e) => {
                const f = MATCH_FORMATS.find((x) => x.id === e.target.value);
                update(f ? { formatId: f.id, clockAt: { ...f.clockAt } } : { formatId: 'custom' });
              }}
            >
              {MATCH_FORMATS.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.label}
                </option>
              ))}
              <option value="custom">Custom</option>
            </select>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
            {needed.map((p) => (
              <ClockField key={p} period={p} value={project.clockAt[p] ?? 0} onChange={(v) => update((pr) => ({ formatId: 'custom', clockAt: { ...pr.clockAt, [p]: v } }))} />
            ))}
          </div>
          <p className="text-small muted" style={{ margin: 0 }}>
            Veo's clock in the 2nd period doesn't always restart at 45:00. If the first clip of a half plays early or late, change its clock value, then use “Check first clip”.
          </p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {missingKickoffs.length > 0 && (
            <span className="text-small muted app-row" style={{ gap: 6 }}>
              <Icon name="info" size={16} />
              Set {missingKickoffs.length === needed.length ? 'the kickoffs' : `the ${missingKickoffs.map(periodName).join(' and ')} kickoff`} to continue.
            </span>
          )}
          <div className="app-row">
            <Button icon="arrow-left" onClick={() => setStep('timestamps')}>
              Back
            </Button>
            <Button variant="primary" icon="arrow-right" iconAfter disabled={missingKickoffs.length > 0} onClick={() => setStep('clips')} style={{ flexGrow: 1 }}>
              Review clips
            </Button>
          </div>
        </div>
      </Panel>
    </div>
  );
}
